import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { placesApi } from '@/features/places/api/placesApi';
import { Place } from '@/types/place';

export interface PlaceFormData {
  name: string;
  description: string;
  coordinates: { latitude: string; longitude: string };
  address: string;
  city: string;
  zip: string;
  country: string;
  type: string;
  age_category: string[];
  tags: string[];
  fee: { price: string; price_type: string };
  kid_friendly: boolean;
  disability_friendly: boolean;
  equipment: {
    personal_equipment_required: boolean;
    equipment_offered: boolean;
    equipment_description: string;
  };
  contacts: {
    phone: string;
    website: string;
    facebook: string;
    instagram: string;
  };
  images: string[];
}

const initialFormData: PlaceFormData = {
  name: '',
  description: '',
  coordinates: { latitude: '', longitude: '' },
  address: '',
  city: '',
  zip: '',
  country: '',
  type: '',
  age_category: [],
  tags: [],
  fee: { price: '', price_type: '' },
  kid_friendly: false,
  disability_friendly: false,
  equipment: {
    personal_equipment_required: false,
    equipment_offered: false,
    equipment_description: '',
  },
  contacts: {
    phone: '',
    website: '',
    facebook: '',
    instagram: '',
  },
  images: [],
};

export const usePlaceForm = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<PlaceFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create place mutation
  const createMutation = useMutation({
    mutationFn: (data: Partial<Place>) => placesApi.create(data),
    onSuccess: () => {
      // Invalidate places query to refetch
      queryClient.invalidateQueries({ queryKey: ['places'] });
      // Navigation handled by component after Snackbar display
    },
  });

  const handleChange = (name: string, value: any) => {
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof PlaceFormData] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCoordinateChange = (field: 'latitude' | 'longitude', value: string) => {
    if (/^-?\d*\.?\d*$/.test(value) || value === '') {
      setFormData((prev) => ({
        ...prev,
        coordinates: {
          ...prev.coordinates,
          [field]: value,
        },
      }));
    }
  };

  const handleMapClick = (coordinates: { latitude: number; longitude: number }) => {
    setFormData((prev) => ({
      ...prev,
      coordinates: {
        latitude: coordinates.latitude?.toString() || '',
        longitude: coordinates.longitude?.toString() || '',
      },
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.coordinates.latitude || !formData.coordinates.longitude) {
      newErrors.coordinates = 'Coordinates are required';
    }
    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const apiPayload = {
      name: formData.name,
      description: formData.description,
      images: formData.images,
      coordinates: {
        latitude: parseFloat(formData.coordinates.latitude),
        longitude: parseFloat(formData.coordinates.longitude),
      },
      address: formData.address,
      city: formData.city,
      zip: formData.zip,
      country: formData.country,
      type: formData.type,
      age_category: formData.age_category.map((category) => category.toLowerCase()),
      tags: formData.tags,
      fee: {
        price: formData.fee.price,
        price_type: formData.fee.price_type,
      },
      kid_friendly: formData.kid_friendly,
      disability_friendly: formData.disability_friendly,
      equipment: {
        personal_equipment_required: formData.equipment.personal_equipment_required,
        equipment_offered: formData.equipment.equipment_offered,
        equipment_description: formData.equipment.equipment_description,
      },
      contacts: {
        phone: formData.contacts.phone,
        website: formData.contacts.website,
        facebook: formData.contacts.facebook,
        instagram: formData.contacts.instagram,
      },
    };

    createMutation.mutate(apiPayload);
  };

  const reset = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  return {
    formData,
    errors,
    handleChange,
    handleCoordinateChange,
    handleMapClick,
    handleSubmit,
    reset,
    isSubmitting: createMutation.isPending,
    isSuccess: createMutation.isSuccess,
    submitError: createMutation.error,
  };
};
